import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
interface RegisterStep {
  id: number;
  title: string;
  component: React.ReactNode;
}
export function Register() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    completeRegistration
  } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: location.state?.email || '',
    businessName: '',
    website: '',
    role: 'Underwriting'
  });
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleNext = async () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      try {
        await completeRegistration({
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          businessName: formData.businessName,
          role: formData.role
        });
        navigate('/invite');
      } catch (error) {
        console.error('Error completing registration:', error);
      }
    }
  };
  const ContactInfoStep = <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200 mb-6 max-w-md w-full" data-id="element-76">
      <h3 className="text-xl font-semibold mb-6" data-id="element-77">What's your contact info?</h3>
      <p className="text-sm text-gray-600 mb-6" data-id="element-78">
        You'll be the account admin when you're creating the account
      </p>
      <div className="space-y-4" data-id="element-79">
        <div data-id="element-80">
          <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-81">
            First name
          </label>
          <input id="firstName" name="firstName" type="text" value={formData.firstName} onChange={handleInputChange} className="w-full p-3 border border-gray-300 rounded-md" required data-id="element-82" />
        </div>
        <div data-id="element-83">
          <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-84">
            Surname
          </label>
          <input id="lastName" name="lastName" type="text" value={formData.lastName} onChange={handleInputChange} className="w-full p-3 border border-gray-300 rounded-md" required data-id="element-85" />
        </div>
        <div data-id="element-86">
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-87">
            Current email address
          </label>
          <input id="email" name="email" type="email" value={formData.email} onChange={handleInputChange} className="w-full p-3 border border-gray-300 rounded-md" required data-id="element-88" />
        </div>
      </div>
      <div className="mt-6" data-id="element-89">
        <button onClick={handleNext} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-md transition duration-150 ease-in-out" data-id="element-90">
          Next
        </button>
      </div>
    </div>;
  const BusinessInfoStep = <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200 mb-6 max-w-md w-full" data-id="element-91">
      <h3 className="text-xl font-semibold mb-6" data-id="element-92">Add your business details</h3>
      <div className="space-y-4" data-id="element-93">
        <div data-id="element-94">
          <label htmlFor="businessName" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-95">
            Business Name
          </label>
          <input id="businessName" name="businessName" type="text" value={formData.businessName} onChange={handleInputChange} className="w-full p-3 border border-gray-300 rounded-md" required data-id="element-96" />
        </div>
        <div data-id="element-97">
          <label htmlFor="website" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-98">
            Website
          </label>
          <input id="website" name="website" type="text" value={formData.website} onChange={handleInputChange} className="w-full p-3 border border-gray-300 rounded-md" placeholder="https://" data-id="element-99" />
        </div>
      </div>
      <div className="mt-6" data-id="element-100">
        <button onClick={handleNext} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-md transition duration-150 ease-in-out" data-id="element-101">
          Next
        </button>
      </div>
    </div>;
  const RoleSelectionStep = <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200 mb-6 max-w-md w-full" data-id="element-102">
      <h3 className="text-xl font-semibold mb-6" data-id="element-103">What's your role?</h3>
      <p className="text-sm text-gray-600 mb-6" data-id="element-104">
        Let's set up the platform for how you work.
      </p>
      <div data-id="element-105">
        <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-106">
          Select role
        </label>
        <select id="role" name="role" value={formData.role} onChange={handleInputChange} className="w-full p-3 border border-gray-300 rounded-md" data-id="element-107">
          <option value="Founder/C-suite" data-id="element-108">Founder/Executive</option>
          <option value="Finance" data-id="element-109">Finance</option>
          <option value="Underwriting" data-id="element-110">Underwriting</option>
          <option value="Captive Manager" data-id="element-111">Captive Manager</option>
          <option value="Other" data-id="element-112">Other</option>
        </select>
      </div>
      <div className="mt-6" data-id="element-113">
        <button onClick={handleNext} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-md transition duration-150 ease-in-out" data-id="element-114">
          Continue
        </button>
        <button onClick={handleNext} className="w-full text-gray-600 font-medium py-3 px-4 rounded-md transition duration-150 ease-in-out mt-2" data-id="element-115">
          Skip
        </button>
      </div>
    </div>;
  const FeasibilityOptionsStep = <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200 mb-6 max-w-md w-full" data-id="element-116">
      <h3 className="text-xl font-semibold mb-6" data-id="element-117">Choose your path</h3>
      <div className="space-y-4" data-id="element-118">
        <div className="border border-gray-200 rounded-lg p-4 hover:border-blue-500 cursor-pointer" data-id="element-119">
          <h4 className="font-medium" data-id="element-120">1. Fast-track feasibility study</h4>
          <p className="text-sm text-gray-600" data-id="element-121">
            Quick assessment of your MGA opportunity
          </p>
        </div>
        <div className="border border-gray-200 rounded-lg p-4 hover:border-blue-500 cursor-pointer" data-id="element-122">
          <h4 className="font-medium" data-id="element-123">2. More Information</h4>
          <p className="text-sm text-gray-600" data-id="element-124">
            Detailed analysis with comprehensive insights
          </p>
        </div>
      </div>
      <div className="mt-6" data-id="element-125">
        <button onClick={handleNext} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-md transition duration-150 ease-in-out" data-id="element-126">
          Continue
        </button>
      </div>
    </div>;
  const steps: RegisterStep[] = [{
    id: 0,
    title: 'Contact Information',
    component: ContactInfoStep
  }, {
    id: 1,
    title: 'Business Details',
    component: BusinessInfoStep
  }, {
    id: 2,
    title: 'Role Selection',
    component: RoleSelectionStep
  }, {
    id: 3,
    title: 'Feasibility Options',
    component: FeasibilityOptionsStep
  }];
  return <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4" data-id="element-127">
      <div className="w-full max-w-4xl" data-id="element-128">
        <div className="text-center mb-12" data-id="element-129">
          <img src="https://cdn.magicpatterns.com/uploads/ssm8opUN7FjpcwsqK5Tnq3/accrue_logo.png" alt="Accrue" className="h-32 mx-auto mb-12" data-id="element-130" />
          <h1 className="text-4xl font-bold mb-2" data-id="element-131">Register Journey</h1>
          <p className="text-gray-600" data-id="element-132">Complete your profile to get started</p>
        </div>
        <div className="flex flex-col items-center" data-id="element-133">
          {/* Progress indicator */}
          <div className="w-full max-w-md mb-8" data-id="element-134">
            <div className="flex justify-between" data-id="element-135">
              {steps.map((step, index) => <div key={step.id} className="flex flex-col items-center" data-id="element-136">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${index <= currentStep ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'}`} data-id="element-137">
                    {index < currentStep ? '✓' : index + 1}
                  </div>
                  {index < steps.length - 1 && <div className={`h-1 w-16 mt-3 ${index < currentStep ? 'bg-blue-600' : 'bg-gray-200'}`} data-id="element-138"></div>}
                </div>)}
            </div>
          </div>
          {steps[currentStep].component}
        </div>
      </div>
    </div>;
}